import { gsap } from "gsap";
import { IDestroyOptions, Sprite } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { Hand } from "../../../common/components/Hand";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { IFocusable } from "../../../layers/IFocusable";
import { CheckOutShopTooltip } from "../components/CheckOutShopTooltip";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { CheckoutShopResourcePackage } from "../resources/CheckoutShopResourcePackage";
import { CheckoutsTooltipResourcePackage } from "../../../common/resources/CheckoutsTooltipResourcePackage";

export class CheckOutShopPopupView extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly commonResources: CommonResourcesPackage;
    private readonly checkoutShopResources: CheckoutShopResourcePackage;
    private readonly checkoutsTooltipResources: CheckoutsTooltipResourcePackage;

    private fadeScreen: FadeScreen;
    private topPanel: TopPanel;
    private light: Sprite;
    private hand: Hand;
    private tooltip: CheckOutShopTooltip;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler,
        topPanel: TopPanel,
        checkoutShopResources: CheckoutShopResourcePackage,
        checkoutsTooltipResources: CheckoutsTooltipResourcePackage
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.topPanel = topPanel;
        this.checkoutShopResources = checkoutShopResources;
        this.checkoutsTooltipResources = checkoutsTooltipResources;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.show.bind(this));
    }

    public start(): void { }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.topPanel.bringCoinFieldToFront();
        this.topPanel.onShopOpen.add(this.hide, this);

        this.light = new Sprite(this.checkoutShopResources.assets_views_checkout_shop_light_yellow_png);
        this.light.anchor.x = 0.5;
        this.addChild(this.light);

        this.hand = new Hand(this.commonResources);
        this.hand.angle = -168;
        this.addChild(this.hand);

        this.tooltip = new CheckOutShopTooltip(this.checkoutsTooltipResources);
        this.addChild(this.tooltip);
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.light.position.set(width * 0.5, height * 0);
        this.hand.scale.set(-0.7, 0.7);
        this.hand.position.set(255, 500);
        this.tooltip.position.set(width * 0.5, 866);
    }

    private async hide(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.to(this, { alpha: 0, duration: 0.5, ease: "power1.out" });
        animationTimeLine.kill();
        this.topPanel.bringCoinFieldToOriginParent();
        this.operationControls.complete()
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.topPanel.onShopOpen.remove(this.hide, this);
        this.topPanel.bringCoinFieldToOriginParent();
        super.destroy(options);
    }
}